import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mail, ArrowLeft, Check } from 'lucide-react';
const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthPage = () => {
  const { t } = useTranslation();
  // view: 'login' | 'register' | 'forgot' | 'reset_code'
  const [view, setView] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [professionalUse, setProfessionalUse] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  // Set English title/meta for .com (overrides Russian defaults from index.html)
  useEffect(() => {
    document.title = 'Sign In — ARPAI GEO | AI Visibility Monitoring';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Sign in or create an account to monitor your brand visibility across AI engines.');
  }, []);
  // --- Google Sign-In ---
  const googleBtnRef = useRef(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const googleCbRef = useRef(null);
  googleCbRef.current = async (gResp) => {
    setError(''); setInfo(''); setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/google`, { credential: gResp.credential });
      if (res.data?.user) { window.location.href = '/dashboard'; }
    } catch (err) { setError(err.response?.data?.detail || 'Google sign-in failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    const cid = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!cid) return;
    window._arpaiGoogleCb = (r) => googleCbRef.current?.(r);
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => {
      window.google?.accounts.id.initialize({ client_id: cid, callback: window._arpaiGoogleCb });
      setGoogleLoaded(true);
    };
    document.head.appendChild(s);
  }, []);
  useEffect(() => {
    if (!googleLoaded || !googleBtnRef.current) return;
    googleBtnRef.current.innerHTML = '';
    window.google?.accounts.id.renderButton(googleBtnRef.current, {
      theme: 'outline', size: 'large', width: 400,
      text: 'signin_with', shape: 'rectangular', logo_alignment: 'left',
    });
  }, [googleLoaded, view]);
  // --- End Google Sign-In ---
  const clearMessages = () => { setError(''); setInfo(''); };
  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) { navigate('/dashboard'); }
      else { setError(result.error); }
    } catch { setError(t('auth.ошибка_входа')); }
    finally { setLoading(false); }
  };
  const handleSendCode = async () => {
    clearMessages();
    if (!email) { setError(t('auth.введите_email')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/send-code`, { email });
      setCodeSent(true);
      setInfo(t('auth.код_отправлен_на') + ' ' + email);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.ошибка_отправки_кода'));
    } finally { setLoading(false); }
  };
  const handleVerifyCode = async () => {
    clearMessages();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-code`, { email, code });
      setEmailVerified(true);
      setCodeSent(false);
      setInfo(t('auth.email_подтверждён_придумайте_пароль_для_завершения'));
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.неверный_код'));
    } finally { setLoading(false); }
  };
  const handleRegister = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!emailVerified) { setError(t('auth.сначала_подтвердите_email')); return; }
    if (!consentAccepted) { setError(t('auth.необходимо_принять_условия')); return; }
    if (password.length < 8 || !/[a-zA-Zа-яА-ЯёЁ]/.test(password) || !/\d/.test(password)) { setError(t('auth.пароль_должен_быть_не_менее_8_символов_и_содержать')); return; }
    setLoading(true);
    try {
      const result = await register(name, email, password, consentAccepted, professionalUse);
      if (result.success) { navigate('/dashboard'); }
      else { setError(result.error); }
    } catch { setError(t('auth.ошибка_регистрации')); }
    finally { setLoading(false); }
  };
  const handleForgotSend = async () => {
    clearMessages();
    if (!email) { setError(t('auth.введите_email')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setInfo(t('auth.если_email_зарегистрирован_код_отправлен'));
      setView('reset_code');
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.ошибка'));
    } finally { setLoading(false); }
  };
  const handleResetPassword = async () => {
    clearMessages();
    if (newPassword.length < 8 || !/[a-zA-Zа-яА-ЯёЁ]/.test(newPassword) || !/\d/.test(newPassword)) { setError(t('auth.пароль_должен_быть_не_менее_8_символов_и_содержать')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { email, code, new_password: newPassword });
      setInfo(t('auth.пароль_изменён_войдите_с_новым_паролем'));
      setView('login');
      setPassword('');
      setCode('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.ошибка_сброса_пароля'));
    } finally { setLoading(false); }
  };
  const switchTo = (v) => { clearMessages(); setView(v); setCode(''); setCodeSent(false); setEmailVerified(false); };
  const renderForm = () => {
    switch (view) {
      case 'login':
        return (
          <>
            <h1 className="text-5xl sm:text-6xl tracking-tighter leading-none font-black text-[#2F2E2F] mb-5">{t('auth.вход')}</h1>
            <p className="text-lg text-slate-600 mb-10">{t('auth.войдите_в_систему_мониторинга')}</p>
            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <>
                <div ref={googleBtnRef} className="flex justify-center mb-3" />
                <p className="text-xs text-slate-400 text-center leading-relaxed mt-1">
                  By continuing with Google, you agree to our{' '}
                  <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline">Privacy Policy</a>
                </p>
                <div className="my-8 flex items-center gap-4">
                  <div className="flex-1 h-px bg-[#E2DDD8]" />
                  <span className="text-sm text-slate-400 uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-[#E2DDD8]" />
                </div>
              </>
            )}
            <form onSubmit={handleLogin} className="space-y-10" data-testid="auth-form">
              <InputField label="EMAIL" type="email" value={email} onChange={setEmail} testId="auth-email-input" />
              <InputField label={t('auth.пароль')} type="password" value={password} onChange={setPassword} testId="auth-password-input" />
              <SubmitBtn loading={loading} text={t('auth.войти')} loadingText={t('auth.загрузка')} testId="auth-submit-button" />
            </form>
            <div className="mt-8 flex justify-between items-center">
              <button onClick={() => switchTo('register')} className="text-[#1BB594] hover:underline font-medium text-base" data-testid="auth-toggle-button">
                {t('auth.нет_аккаунта')}
              </button>
              <button onClick={() => switchTo('forgot')} className="text-slate-500 hover:underline text-base" data-testid="forgot-password-link">
                {t('auth.забыли_пароль')}
              </button>
            </div>
          </>
        );
      case 'register':
        return (
          <>
            <h1 className="text-5xl sm:text-6xl tracking-tighter leading-none font-black text-[#2F2E2F] mb-5">{t('auth.регистрация')}</h1>
            <p className="text-lg text-slate-600 mb-10">
              {!codeSent && !emailVerified && t('auth.укажите_имя_и_email_для_начала')}
              {codeSent && !emailVerified && t('auth.введите_код_из_письма')}
              {emailVerified && t('auth.придумайте_пароль_и_завершите_регистрацию')}
            </p>
            {!codeSent && !emailVerified && process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <>
                <div ref={googleBtnRef} className="flex justify-center mb-3" />
                <p className="text-xs text-slate-400 text-center leading-relaxed mt-1">
                  By continuing with Google, you agree to our{' '}
                  <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline">Privacy Policy</a>
                </p>
                <div className="my-8 flex items-center gap-4">
                  <div className="flex-1 h-px bg-[#E2DDD8]" />
                  <span className="text-sm text-slate-400 uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-[#E2DDD8]" />
                </div>
              </>
            )}
            <form onSubmit={handleRegister} className="space-y-8" data-testid="auth-form">
              {/* Step 1: Name and Email */}
              <InputField label={t('auth.имя')} type="text" value={name} onChange={setName} testId="auth-name-input" required disabled={codeSent || emailVerified} />
              <div>
                <InputField label="EMAIL" type="email" value={email} onChange={setEmail} testId="auth-email-input" disabled={codeSent || emailVerified} />
                {!codeSent && !emailVerified && (
                  <>
                  <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                    {t('auth.email_consent_prefix')}{' '}
                    <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline">Terms of Service</a>
                    {' '}{t('auth.и')}{' '}
                    <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline">Privacy Policy</a>
                  </p>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={loading || !email || !name}
                    className="mt-4 w-full rounded-none font-bold tracking-widest uppercase text-sm px-8 py-5 bg-[#1BB594] hover:bg-[#159A7F] text-white disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="send-code-btn"
                  >
                    <Mail className="w-5 h-5" /> {t('auth.получить_код')}
                  </button>
                  </>
                )}
                {emailVerified && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-600 font-medium" data-testid="email-verified-badge">{t('auth.email_подтверждён')}</span>
                  </div>
                )}
              </div>

              {/* Step 2: Code verification (inline) */}
              {codeSent && !emailVerified && (
                <div className="p-5 bg-emerald-50 border border-emerald-200 space-y-5">
                  <InputField label={t('auth.код_из_письма')} type="text" value={code} onChange={setCode} testId="verification-code-input" maxLength={6} />
                  <button
                    type="button"
                    onClick={handleVerifyCode}
                    disabled={loading || code.length < 4}
                    className="w-full rounded-none font-bold tracking-widest uppercase text-sm px-8 py-5 bg-[#1BB594] hover:bg-[#159A7F] text-white disabled:opacity-50"
                    data-testid="verify-code-btn"
                  >
                    {loading ? t('auth.проверка') : t('auth.подтвердить_код')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={loading}
                    className="w-full text-sm text-slate-500 hover:underline"
                  >
                    {t('auth.отправить_повторно')}
                  </button>
                </div>
              )}

              {/* Step 3: Password and finish (only after email verification) */}
              {emailVerified && (
                <>
                  <InputField label={t('auth.пароль')} type="password" value={password} onChange={setPassword} testId="auth-password-input" />
                  <p className="text-sm text-slate-400 -mt-5">{t('auth.минимум_8_символов_буквы_и_цифры')}</p>
                  <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="consent-checkbox-label">
                    <input
                      type="checkbox"
                      checked={consentAccepted}
                      onChange={(e) => setConsentAccepted(e.target.checked)}
                      className="w-5 h-5 mt-0.5 accent-[#1BB594] flex-shrink-0"
                      data-testid="consent-checkbox"
                    />
                    <span className="text-sm text-slate-600 leading-relaxed">
                      {t('auth.принимаю_условия_prefix')}{' '}
                      <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline font-medium">{t('auth.договораоферты')}</a>
                      {' '}{t('auth.и_даю_согласие')}{' '}
                      <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-[#1BB594] hover:underline font-medium">{t('auth.обработку_персональных_данных')}</a>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="professional-checkbox-label">
                    <input
                      type="checkbox"
                      checked={professionalUse}
                      onChange={(e) => setProfessionalUse(e.target.checked)}
                      className="w-5 h-5 mt-0.5 accent-[#1BB594] flex-shrink-0"
                      data-testid="professional-checkbox"
                    />
                    <span className="text-sm text-slate-600 leading-relaxed">
                      {t('auth.professional_use')}
                    </span>
                  </label>
                  <SubmitBtn loading={loading} text={t('auth.зарегистрироваться')} loadingText={t('auth.загрузка')} disabled={!consentAccepted || !professionalUse} testId="auth-submit-button" />
                </>
              )}
            </form>
            <div className="mt-8">
              <button onClick={() => switchTo('login')} className="text-[#1BB594] hover:underline font-medium text-base" data-testid="auth-toggle-button">
                {t('auth.уже_есть_аккаунт')}
              </button>
            </div>
          </>
        );
      case 'forgot':
        return (
          <>
            <h1 className="text-5xl tracking-tighter font-black text-[#2F2E2F] mb-5">{t('auth.сброс_пароля')}</h1>
            <p className="text-lg text-slate-600 mb-10">{t('auth.введите_email_для_получения_кода_сброса')}</p>
            <div className="space-y-8">
              <InputField label="EMAIL" type="email" value={email} onChange={setEmail} testId="forgot-email-input" />
              <button
                onClick={handleForgotSend}
                disabled={loading || !email}
                className="w-full rounded-none font-bold tracking-widest uppercase text-sm px-8 py-5 bg-[#1BB594] hover:bg-[#159A7F] text-white disabled:opacity-50"
                data-testid="forgot-send-btn"
              >
                {loading ? t('auth.отправка') : t('auth.отправить_код')}
              </button>
            </div>
            <button onClick={() => switchTo('login')} className="mt-6 text-slate-500 hover:underline text-base">
              <ArrowLeft className="w-4 h-4 inline mr-1" />{t('auth.назад_к_входу')}
            </button>
          </>
        );
      case 'reset_code':
        return (
          <>
            <h1 className="text-5xl tracking-tighter font-black text-[#2F2E2F] mb-5">{t('auth.новый_пароль')}</h1>
            <p className="text-lg text-slate-600 mb-10">{t('auth.введите_код_из_письма_и_новый_пароль')}</p>
            <div className="space-y-8">
              <InputField label={t('auth.код_из_письма')} type="text" value={code} onChange={setCode} testId="reset-code-input" maxLength={6} />
              <InputField label={t('auth.новый_пароль')} type="password" value={newPassword} onChange={setNewPassword} testId="new-password-input" />
              <button
                onClick={handleResetPassword}
                disabled={loading || code.length < 4 || newPassword.length < 8}
                className="w-full rounded-none font-bold tracking-widest uppercase text-sm px-8 py-5 bg-[#1BB594] hover:bg-[#159A7F] text-white disabled:opacity-50"
                data-testid="reset-password-btn"
              >
                {loading ? t('auth.сброс') : t('auth.изменить_пароль')}
              </button>
            </div>
            <button onClick={() => switchTo('login')} className="mt-6 text-slate-500 hover:underline text-base">
              <ArrowLeft className="w-4 h-4 inline mr-1" />{t('auth.назад_к_входу')}
            </button>
          </>
        );
      default:
        return null;
    }
  };
  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end justify-center"
        style={{ backgroundImage: 'url(/arpai_full.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <p
          className="mb-20 px-12 text-center text-2xl tracking-[0.25em] uppercase"
          style={{ color: '#C8B9A0', fontFamily: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif', fontWeight: 600, letterSpacing: '0.25em', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
        >
          {t('auth.лаборатория_слоган')}
        </p>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 md:px-16 bg-[#FFFFFF]">
        <div className="w-full max-w-xl">
          <div className="mb-12">
            <div className="flex items-baseline gap-2">
              <img src="/arpai_logo.png" alt="ARPAI" style={{ height: '2.5rem' }} />
              <span className="text-[2.5rem] font-black text-[#2F2E2F] tracking-[0.15em]">GEO</span>
            </div>
            <p className="text-base text-slate-500 mt-3 tracking-wide">{t('auth.сервис_мониторинга_и_аналитики_в_ai_сетях')}</p>
          </div>
          {error && (
            <div className="mb-8 p-5 bg-red-50 border border-red-200 text-red-700 rounded-none text-base" data-testid="auth-error-message">
              {error}
            </div>
          )}
          {info && (
            <div className="mb-8 p-5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-none text-base" data-testid="auth-info-message">
              {info}
            </div>
          )}
          {renderForm()}
        </div>
      </div>
    </div>
  );
};
const InputField = ({ label, type, value, onChange, testId, required, disabled, maxLength }) => (
  <div>
    <label className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 block mb-3">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E2DDD8] bg-transparent focus:ring-0 focus:border-[#1BB594] px-0 py-3 text-xl ${disabled ? 'opacity-50' : ''}`}
      required={required}
      disabled={disabled}
      maxLength={maxLength}
      data-testid={testId}
    />
  </div>
);
const SubmitBtn = ({ loading, text, loadingText, disabled, testId }) => (
  <button
    type="submit"
    disabled={loading || disabled}
    className="w-full rounded-none font-bold tracking-widest uppercase text-sm px-8 py-5 bg-[#1BB594] hover:bg-[#159A7F] text-white transition-transform active:translate-y-1 disabled:opacity-50"
    data-testid={testId}
  >
    {loading ? (loadingText || 'LOADING...') : text}
  </button>
);
export default AuthPage;
